import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@material-ui/core';
import { Block } from '@material-ui/icons';
import { GlobalContext } from 'globalContext/GlobalContext';
import { UserContext } from 'globalContext/UserContext';
import TableLoadingWrapper from 'components/TableLoadingWrapper';
import { displayLabelRows } from 'utils/displayLabelRows';
import { useToggleState } from 'utils/hooks/useToggleState';
import { HoursCell } from './HoursCell';
import { getAllocationOrderLabel } from './orderPresentation';
import { Report, TableHead, TableProps } from './types';
import {
  Paper,
  SortAsc,
  SortDesc,
  Table as TableComponent,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader as TableHeadComponent,
  TablePagination,
  TableRow,
  Unsorted,
} from './Table.css';

const Table = ({
  sorting = { column: 'name', direction: 'asc' },
  setRowsPerPage = () => {
    // intentional empty function
  },
  setCurrentPage = () => {
    // intentional empty function
  },
  setSorting = () => {
    // intentional empty function
  },
  withPagination = false,
  loading = false,
  currentPage = 0,
  pageCount = 1,
  limit,
  head,
  data,
  toggleSorting,
  toggleWorktimeDisplay,
}: TableProps) => {
  const intl = useIntl();
  const [hoursChecked, handleChange] = useToggleState(false);
  const {
    localization: { currentLocale },
  } = useContext(GlobalContext);
  const { roles } = useContext(UserContext);
  const renderHoursCellData = (entry: Report) =>
    hoursChecked ? entry.workInHours : entry.workInDays;

  const changeTimeDisplay = () => {
    handleChange();
    toggleWorktimeDisplay &&
      toggleWorktimeDisplay((displayDays) => !displayDays);
  };

  const renderLabel = (headData: TableHead) => {
    if (headData.key === 'reportedHours') {
      return HoursCell(hoursChecked, intl, changeTimeDisplay);
    }

    return intl.formatMessage({ id: headData.label });
  };

  const renderCellData = (key: keyof Report, entry: Report) => {
    if (key === 'orderId') {
      const label = getAllocationOrderLabel({
        orderId: entry.orderId ?? null,
        orderStatus: entry.orderStatus,
      });
      if (label === 'ORDER_PRESENTATION.MISSING_ASSIGNMENT') {
        // Only these hours need follow-up before settlement (D10).
        return (
          <Box component="span" color="error.main" fontWeight="fontWeightBold">
            {intl.formatMessage({ id: label })}
          </Box>
        );
      }
      return label
        ? intl.formatMessage({ id: label })
        : entry.orderNumber || '—';
    }
    if (
      key === 'reportedHours' &&
      entry.workInHours > 0 &&
      entry.workInDays > 0
    ) {
      return renderHoursCellData(entry);
    }
    if (key === 'multiplier' && entry.multiplier !== undefined) {
      return `${entry.multiplicator * 100}%`;
    }
    if (key === 'unitType') {
      return entry.workUnitTypes;
    }
    if (key === 'reportedUnits' && entry.workUnits > 0) {
      return entry.workUnits;
    }
    if (key === 'travelBy') {
      return entry.travelBy
        ? intl.formatMessage({
            id: `BUSINESS_TRIP.${entry.travelBy}`,
          })
        : '-';
    }
    return entry[key];
  };

  const renderSortingIcon = (
    column: string,
    sortable: boolean,
    sorting: { column: string; direction: 'asc' | 'desc' },
  ) => {
    const sortByColumn = column === 'name' ? 'surname' : column;

    if (!sortable) {
      return null;
    }
    if (
      sortable &&
      sortByColumn === sorting.column &&
      sorting.direction === 'asc'
    ) {
      return (
        <SortAsc
          onClick={() => toggleSorting(sortByColumn, sorting, setSorting)}
          active={sortByColumn === sorting.column ? 1 : 0}
          id={`${sortByColumn}_sortAsc`}
        />
      );
    }
    if (
      sortable &&
      sortByColumn === sorting.column &&
      sorting.direction === 'desc'
    ) {
      return (
        <SortDesc
          onClick={() => toggleSorting(sortByColumn, sorting, setSorting)}
          active={sortByColumn === sorting.column ? 1 : 0}
          id={`${sortByColumn}_sortDesc`}
        />
      );
    }
    return (
      <Unsorted
        onClick={() => toggleSorting(sortByColumn, sorting, setSorting)}
        id={`${sortByColumn}_unsorted`}
      />
    );
  };

  return (
    <TableContainer component={Paper} $loading={loading}>
      {withPagination && !!limit && (
        <TablePagination
          onChangeRowsPerPage={setRowsPerPage}
          onPageChange={setCurrentPage}
          labelRowsPerPage={intl.formatMessage({
            id: 'TABLE.PAGINATION.ROWS_PER_PAGE',
          })}
          labelDisplayedRows={({ from, to, count }) =>
            displayLabelRows({ from, to, count, intl })
          }
          rowsPerPageOptions={[]}
          rowsPerPage={limit}
          page={currentPage}
          count={pageCount}
          //@ts-ignore
          component={'div'}
        />
      )}
      <TableComponent>
        <TableLoadingWrapper loading={loading}>
          <>
            <TableHeadComponent>
              <TableRow $main>
                {head.map(
                  (headData, index) =>
                    roles.includes(headData.role) && (
                      <TableCell
                        align={headData.align}
                        key={`${index} - ${headData.key}`}
                      >
                        {renderLabel(headData)}
                        {renderSortingIcon(
                          headData.key,
                          headData.sortable,
                          sorting,
                        )}
                      </TableCell>
                    ),
                )}
              </TableRow>
            </TableHeadComponent>

            <TableBody>
              {data.length ? (
                data.map((entry: Report, index: number) => (
                  <TableRow key={`${entry.name}-${index}`} index={index}>
                    {head.map(
                      ({ key, role }, headIndex: number) =>
                        roles.includes(role) && (
                          <TableCell
                            align={
                              head.length ? head[headIndex].align : undefined
                            }
                            key={`${entry.name.concat(
                              key,
                            )}-${index}-${headIndex}-${currentLocale}`}
                            component="th"
                            scope="row"
                          >
                            {renderCellData(key, entry)}
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align={'center'}>
                    <Block />
                    <p>{intl.formatMessage({ id: 'TABLE.BODY.EMPTY_DATA' })}</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </>
        </TableLoadingWrapper>
      </TableComponent>
      {withPagination && !!limit && (
        <TablePagination
          onChangeRowsPerPage={setRowsPerPage}
          onPageChange={setCurrentPage}
          labelRowsPerPage={intl.formatMessage({
            id: 'TABLE.PAGINATION.ROWS_PER_PAGE',
          })}
          labelDisplayedRows={({ from, to, count }) =>
            displayLabelRows({ from, to, count, intl })
          }
          rowsPerPageOptions={[10, 20, 50, 100]}
          rowsPerPage={limit}
          page={currentPage}
          count={pageCount}
          //@ts-ignore
          component={'div'}
        />
      )}
    </TableContainer>
  );
};

export default Table;
